import type { MemberId } from "../member-identity";
import type {
  EnablementCondition,
  QuestionnaireAnswerFact,
  QuestionnaireConditionBehavior,
  QuestionnaireConditionOperator,
  QuestionnaireConditionValue,
} from "../questionnaire-definition.model";
import { factValue, isFactCondition, type PatientFactSource } from "./patient-facts";

/**
 * Evaluation of questionnaire conditions over recorded answers: one evaluator
 * for a form's enablement (sections and questions shown or hidden) and for a
 * flow's branches (which questionnaire comes next).
 */
export interface ConditionSet {
  conditions?: readonly EnablementCondition[];
  conditionBehavior?: QuestionnaireConditionBehavior;
}

/** Index answers by question, the shape every evaluation reads. */
export function answersByQuestion(
  answers: readonly QuestionnaireAnswerFact[],
): ReadonlyMap<MemberId, QuestionnaireAnswerFact[]> {
  const byQuestion = new Map<MemberId, QuestionnaireAnswerFact[]>();
  for (const answer of answers) {
    const bucket = byQuestion.get(answer.questionId);
    if (bucket) bucket.push(answer);
    else byQuestion.set(answer.questionId, [answer]);
  }
  return byQuestion;
}

/** Whether a condition set holds. No conditions holds trivially. */
export function conditionsHold(
  holder: ConditionSet,
  answers: ReadonlyMap<MemberId, QuestionnaireAnswerFact[]>,
  facts: PatientFactSource,
): boolean {
  return holds(holder, (question) => answers.get(question) ?? [], facts);
}

function holds(
  holder: ConditionSet,
  answersTo: (question: MemberId) => readonly QuestionnaireAnswerFact[],
  facts: PatientFactSource,
): boolean {
  const conditions = holder.conditions ?? [];
  if (conditions.length === 0) return true;
  const results = conditions.map((condition) => {
    if (isFactCondition(condition))
      return compare(factValue(condition.fact, facts), condition.value, condition.operator);
    const recorded = answersTo(condition.question);
    if (condition.operator === "exists") {
      const exists = recorded.some((answer) => answer.value !== undefined);
      return exists === (condition.value.kind === "Boolean" && condition.value.value);
    }
    const expected = operand(condition.value);
    return recorded.some((answer) => compare(answer.value, expected, condition.operator));
  });
  return holder.conditionBehavior === "any" ? results.some(Boolean) : results.every(Boolean);
}

function operand(value: QuestionnaireConditionValue): boolean | number | string {
  return value.kind === "Choice" ? value.optionId : value.value;
}

function compare(
  actual: boolean | number | string | undefined,
  expected: boolean | number | string,
  operator: QuestionnaireConditionOperator,
): boolean {
  if (actual === undefined) return false;
  if (operator === "=") return actual === expected;
  if (operator === "!=") return actual !== expected;
  // Ordering only means something between two values of the same ordered type.
  // `true > 0` is a JavaScript answer, not a clinical one.
  if (typeof actual !== typeof expected || (typeof actual !== "number" && typeof actual !== "string")) return false;
  if (operator === ">") return actual > expected;
  if (operator === "<") return actual < expected;
  if (operator === ">=") return actual >= expected;
  if (operator === "<=") return actual <= expected;
  return false;
}

// ============================================================
// Form enablement
// ============================================================

/** The parts of a definition enablement reads. Structural, so a read-side DTO can be walked too. */
export interface EnablementQuestion extends ConditionSet {
  kind: "Question";
  id?: MemberId;
}

export type EnablementItem =
  | EnablementQuestion
  | { kind: "Display" }
  | { kind: "RepeatingGroup"; items: readonly (EnablementQuestion | { kind: "Display" })[] };

export interface EnablementSection extends ConditionSet {
  id?: MemberId;
  items: readonly EnablementItem[];
}

/**
 * The sections and questions a form currently hides, given the answers so far.
 *
 * ONE EVALUATOR FOR BOTH SIDES. The renderer hides with it and completion
 * prunes and excuses with it, so a question the patient cannot see is never
 * one the server still demands.
 *
 * Enablement composes the way FHIR says it does:
 *  - a question in a disabled section is disabled with it, and
 *  - a condition that tests a DISABLED question reads it as unanswered, even
 *    though its answer is still on the record (answers are kept until
 *    completion so toggling back is not destructive). Without this, an answer
 *    left behind on a question that is no longer asked would keep enabling
 *    whatever depends on it.
 *
 * Dependencies are acyclic (the definition refuses a cycle), so the recursion
 * terminates; the `pending` guard only keeps a hand-written row from hanging a
 * render, by reading a cyclic reference's answers as recorded.
 */
export function disabledMemberIds(
  sections: readonly EnablementSection[],
  answers: readonly QuestionnaireAnswerFact[],
  facts: PatientFactSource,
): ReadonlySet<MemberId> {
  const byQuestion = answersByQuestion(answers);
  const owners = new Map<MemberId, { question: EnablementQuestion; section: EnablementSection }>();
  for (const section of sections) {
    for (const member of questionsOf(section)) if (member.id) owners.set(member.id, { question: member, section });
  }

  const sectionEnabled = new Map<EnablementSection, boolean>();
  const questionEnabled = new Map<MemberId, boolean>();
  const pending = new Set<EnablementSection | MemberId>();

  const answersTo = (question: MemberId): readonly QuestionnaireAnswerFact[] => {
    const recorded = byQuestion.get(question) ?? [];
    if (!owners.has(question) || pending.has(question)) return recorded;
    return isQuestionEnabled(question) ? recorded : [];
  };
  const isSectionEnabled = (section: EnablementSection): boolean => {
    const known = sectionEnabled.get(section);
    if (known !== undefined) return known;
    if (pending.has(section)) return true;
    pending.add(section);
    const enabled = holds(section, answersTo, facts);
    pending.delete(section);
    sectionEnabled.set(section, enabled);
    return enabled;
  };
  const isQuestionEnabled = (id: MemberId): boolean => {
    const known = questionEnabled.get(id);
    if (known !== undefined) return known;
    const owner = owners.get(id);
    if (!owner) return true;
    pending.add(id);
    const enabled = isSectionEnabled(owner.section) && holds(owner.question, answersTo, facts);
    pending.delete(id);
    questionEnabled.set(id, enabled);
    return enabled;
  };

  const disabled = new Set<MemberId>();
  for (const section of sections) {
    if (section.id && !isSectionEnabled(section)) disabled.add(section.id);
    for (const member of questionsOf(section)) {
      if (member.id && !isQuestionEnabled(member.id)) disabled.add(member.id);
    }
  }
  return disabled;
}

function questionsOf(section: EnablementSection): EnablementQuestion[] {
  return section.items.flatMap((item) => {
    if (item.kind === "Question") return [item];
    if (item.kind === "RepeatingGroup")
      return item.items.filter((member): member is EnablementQuestion => member.kind === "Question");
    return [];
  });
}
